import { useState, useEffect } from "react";
import { Modal, FormLayout, RadioButton, Button, BlockStack, Text, Box, TextField, Thumbnail } from "@shopify/polaris";
import { CollectionIcon } from "@shopify/polaris-icons";
import { SingleTriggeredRewardTier } from "../../CartTypes";
import CloseIcon from "../../CartIcons/CloseIcon";
import styles from "./CartFormCards.module.css";
import { ProductObj, VariantsObj } from "../../../Bundles/Types";

const ConfigureTriggerRewardModal = ({
    isOpen,
    onClose,
    onSave,
    initialData,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSave: (tier: SingleTriggeredRewardTier) => void;
    initialData: SingleTriggeredRewardTier | null;
}) => {
    const [triggerType, setTriggerType] = useState<"product" | "collection">("product");
    const [selectedTriggers, setSelectedTriggers] = useState<any[]>([]); // Products or Collections
    const [freeGifts, setFreeGifts] = useState<ProductObj[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [localQuantities, setLocalQuantities] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            setTriggerType(initialData?.trigger_type || "product");
            setSelectedTriggers(
                initialData?.trigger_type === "product" ? initialData.products || [] : initialData?.collections || [],
            );
            setFreeGifts(initialData?.free_gifts || []);
            // Initialize local quantities for smooth typing
            const qtyMap: Record<string, string> = {};
            (initialData?.free_gifts || []).forEach((g) => {
                qtyMap[g.id] = (g.quantity || 1).toString();
            });
            setLocalQuantities(qtyMap);
            setErrors({});
        }
    }, [isOpen, initialData]);

    const handleSelectTriggers = async () => {
        const type = triggerType === "product" ? "product" : "collection";
        const preSelected = selectedTriggers.map((t) => ({ id: t.id }));

        const selected = await shopify.resourcePicker({
            type,
            multiple: type === "product" ? true : 1,
            selectionIds: preSelected,
            filter: {
                query: type === "product" ? "status:active AND published_status:published" : "",
                variants: false,
            },
        });

        if (selected) {
            if (type === "product") {
                const productsArr: ProductObj[] = [];
                selected?.map((p: any) => {
                    productsArr.push({
                        id: p?.id,
                        images: p?.images,
                        title: p?.title,
                        handle: p?.handle,
                        quantity: 1,
                        maxProductStock: p?.totalInventory,
                        selectedVariants: [],
                        subscriptions: [],
                        oneTimePurchase: true,
                        isSubscribed: false,
                        variants: p?.variants?.map((variant: VariantsObj) => ({
                            image: { ...variant.image },
                            id: variant?.id,
                            displayName: variant.displayName,
                            title: variant?.title?.toLowerCase() == "default title" ? p?.title : variant?.title,
                            price: variant?.price,
                            quantity: 1,
                            selectedOptions: variant?.selectedOptions,
                            copyPrice: variant.price,
                            compareAtPrice: variant?.compareAtPrice ? variant.compareAtPrice : variant.price,
                        })),
                        options: p?.options,
                    });
                });
                setSelectedTriggers(productsArr);
            } else if (type === "collection") {
                const collectionArr: any[] = [];
                selected?.map((c: any) => {
                    collectionArr.push({
                        id: c?.id,
                        image: c?.image?.originalSrc || "",
                        title: c.title,
                    });
                });
                setSelectedTriggers(collectionArr);
            }
        }
    };

    const handleSelectGifts = async () => {
        const preSelected = freeGifts.map((g) => ({
            id: g.id,
            variants: g.variants?.map((v: any) => ({ id: v.id })) || [],
        }));

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: true,
            },
        });

        if (selected) {
            const productsArr: ProductObj[] = []
            selected?.map((p: any) => {
                const existing = freeGifts.find((pg) => pg.id === p.id);
                productsArr.push({
                    id: p?.id,
                    images: p?.images,
                    title: p?.title,
                    handle: p?.handle,
                    quantity: existing ? existing.quantity : 1,
                    maxProductStock: p?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: p?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,
                        title: variant?.title?.toLowerCase() == "default title" ? p?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant?.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: p?.options,
                });
            });
            setFreeGifts(productsArr);
            // Sync local quantities
            const newQtyMap = { ...localQuantities };
            productsArr.forEach((g) => {
                if (!newQtyMap[g.id]) newQtyMap[g.id] = (g.quantity || 1).toString();
            });
            setLocalQuantities(newQtyMap);
        }
    };

    const updateGiftQuantity = (id: string, val: string) => {
        setLocalQuantities((prev) => ({ ...prev, [id]: val }));

        const numVal = parseInt(val);
        if (!isNaN(numVal) && numVal > 0) {
            setFreeGifts((prev) => prev.map((g) => (g.id === id ? { ...g, quantity: numVal } : g)));
        }
    };

    const handleQuantityBlur = (id: string) => {
        const val = localQuantities[id];
        const numVal = parseInt(val);
        if (isNaN(numVal) || numVal < 1) {
            setLocalQuantities((prev) => ({ ...prev, [id]: "1" }));
            setFreeGifts((prev) => prev.map((g) => (g.id === id ? { ...g, quantity: 1 } : g)));
        }
    };

    const removeGift = (id: string) => {
        setFreeGifts((prev) => prev.filter((g) => g.id !== id));
    };

    const removeTrigger = (id: string) => {
        setSelectedTriggers((prev) => prev.filter((t) => t.id !== id));
    };

    const handleSave = () => {
        const newErrors: Record<string, string> = {};
        if (selectedTriggers.length === 0) {
            newErrors.triggers = `Please select at least one ${triggerType === "product" ? "product" : "collection"}.`;
        }
        if (freeGifts.length === 0) {
            newErrors.gifts = "Please select at least one free gift.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave({
            trigger_type: triggerType,
            products: triggerType === "product" ? selectedTriggers : [],
            collections: triggerType === "collection" ? selectedTriggers : [],
            free_gifts: freeGifts,
        });
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            title="Configure Triggered Reward"
            primaryAction={{
                content: initialData ? "Update" : "Create",
                onAction: handleSave,
                disabled: selectedTriggers.length === 0 || freeGifts.length === 0,
            }}
            secondaryActions={[
                {
                    content: "Cancel",
                    onAction: onClose,
                },
            ]}
        >
            <Modal.Section>
                <FormLayout>
                    <BlockStack gap="300">
                        <Text variant="headingSm" as="h3">
                            Trigger Conditions
                        </Text>
                        <BlockStack gap="200">
                            <RadioButton
                                label="Specific products"
                                checked={triggerType === "product"}
                                id="product"
                                name="trigger_type"
                                onChange={() => {
                                    setTriggerType("product");
                                    setSelectedTriggers([]);
                                }}
                            />
                            {triggerType === "product" && (
                                <Box paddingInlineStart="600">
                                    <BlockStack gap="200">
                                        {selectedTriggers.length > 0 && (
                                            <div className={styles.selectionContainer}>
                                                {selectedTriggers.map((t) => (
                                                    <div key={t.id} className={styles.selectionItem}>
                                                        <div className={styles.itemMain}>
                                                            <div className={styles.thumbnailWrapper}>
                                                                <Thumbnail
                                                                    source={
                                                                        t.images?.[0]?.originalSrc ||
                                                                        t.image?.originalSrc ||
                                                                        CollectionIcon
                                                                    }
                                                                    alt={t.title}
                                                                    size="small"
                                                                />
                                                            </div>
                                                            <div className={styles.itemDetails}>
                                                                <div className={styles.itemTitle}>{t.title}</div>
                                                                {t.variants?.length > 1 && (
                                                                    <div className={styles.itemSubtitle}>
                                                                        {t.variants.length} variants selected
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={styles.itemActions}>
                                                            <div
                                                                className={styles.removeAction}
                                                                onClick={() => removeTrigger(t.id)}
                                                            >
                                                                <CloseIcon />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div>
                                            <Button onClick={handleSelectTriggers} variant="secondary" size="slim">
                                                Browse Products
                                            </Button>
                                        </div>
                                        {errors.triggers && triggerType === "product" && (
                                            <Text variant="bodySm" as="p" tone="critical">
                                                {errors.triggers}
                                            </Text>
                                        )}
                                    </BlockStack>
                                </Box>
                            )}

                            <RadioButton
                                label="Specific collections"
                                checked={triggerType === "collection"}
                                id="collection"
                                name="trigger_type"
                                onChange={() => {
                                    setTriggerType("collection");
                                    setSelectedTriggers([]);
                                }}
                            />
                            {triggerType === "collection" && (
                                <Box paddingInlineStart="600">
                                    <BlockStack gap="200">
                                        {selectedTriggers.length > 0 && (
                                            <div className={styles.selectionContainer}>
                                                {selectedTriggers.map((t) => (
                                                    <div key={t.id} className={styles.selectionItem}>
                                                        <div className={styles.itemMain}>
                                                            <div className={styles.thumbnailWrapper}>
                                                                <Thumbnail
                                                                    source={t.image?.originalSrc || CollectionIcon}
                                                                    alt={t.title}
                                                                    size="small"
                                                                />
                                                            </div>
                                                            <div className={styles.itemDetails}>
                                                                <div className={styles.itemTitle}>{t.title}</div>
                                                            </div>
                                                        </div>
                                                        <div className={styles.itemActions}>
                                                            <div
                                                                className={styles.removeAction}
                                                                onClick={() => removeTrigger(t.id)}
                                                            >
                                                                <CloseIcon />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div>
                                            <Button onClick={handleSelectTriggers} variant="secondary" size="slim">
                                                Browse Collections
                                            </Button>
                                        </div>
                                        {errors.triggers && triggerType === "collection" && (
                                            <Text variant="bodySm" as="p" tone="critical">
                                                {errors.triggers}
                                            </Text>
                                        )}
                                    </BlockStack>
                                </Box>
                            )}
                        </BlockStack>

                        <div style={{ margin: "5px 0", borderTop: "1px dashed #e3e3e3" }} />

                        <Text variant="headingSm" as="h3">
                            Reward
                        </Text>

                        {freeGifts.length > 0 ? (
                            <BlockStack gap="200">
                                <div className={styles.selectionContainer}>
                                    {freeGifts.map((gift) => (
                                        <div key={gift.id} className={styles.selectionItem}>
                                            <div className={styles.itemMain}>
                                                <div className={styles.thumbnailWrapper}>
                                                    <Thumbnail
                                                        source={gift.images?.[0]?.originalSrc || CollectionIcon}
                                                        alt={gift.title}
                                                        size="small"
                                                    />
                                                </div>
                                                <div className={styles.itemDetails}>
                                                    <div className={styles.itemTitle}>{gift.title}</div>
                                                    {gift.variants?.length > 1 && (
                                                        <div className={styles.itemSubtitle}>
                                                            {gift.variants.length} variants selected
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={styles.itemActions}>
                                                {/* <div className={styles.qtyInputWrapper}>
                                                    <TextField
                                                        label=""
                                                        labelHidden
                                                        type="number"
                                                        placeholder="Qty"
                                                        value={localQuantities[gift.id] || "1"}
                                                        onChange={(val) => updateGiftQuantity(gift.id, val)}
                                                        onBlur={() => handleQuantityBlur(gift.id)}
                                                        autoComplete="off"
                                                        suffix="Qty"
                                                        size="slim"
                                                        min={1}
                                                    />
                                                </div> */}
                                                <div
                                                    className={styles.removeAction}
                                                    onClick={() => removeGift(gift.id)}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {errors.gifts && (
                                    <Text variant="bodySm" as="p" tone="critical">
                                        {errors.gifts}
                                    </Text>
                                )}
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <Button onClick={handleSelectGifts} variant="tertiary">
                                        Add another gift
                                    </Button>
                                </div>
                            </BlockStack>
                        ) : (
                            <div
                                style={{
                                    border: "1px dashed #ccc",
                                    padding: "30px",
                                    borderRadius: "8px",
                                    textAlign: "center",
                                }}
                            >
                                <Button onClick={handleSelectGifts} variant="secondary">
                                    Add Free Gift Products
                                </Button>
                            </div>
                        )}
                    </BlockStack>
                </FormLayout>
            </Modal.Section>
        </Modal>
    );
};

export default ConfigureTriggerRewardModal;
